/**
 * UI Helper Functions
 *
 * Centralized utilities for creating toasts, modals, loading indicators,
 * and other UI elements. Reduces code duplication and ensures consistent styling.
 */

import { CONFIG } from '../config.js';

/**
 * Show a toast notification
 * @param {string} message - Message to display
 * @param {Object} options - Configuration options
 * @param {string} options.type - Toast type: 'info', 'success', 'error' (default: 'info')
 * @param {string} options.icon - FontAwesome icon class (e.g., 'fa-check-circle')
 * @param {number} options.duration - Duration in milliseconds (default: CONFIG.TOAST_DURATION)
 */
export function showToast(message, options = {}) {
  const {
    type = 'info',
    icon = null,
    duration = CONFIG.TOAST_DURATION
  } = options;

  // Remove existing toast if any
  const existingToast = document.querySelector('.toast-notification');
  if (existingToast) {
    existingToast.remove();
  }

  // Color scheme based on type
  const colors = {
    info: CONFIG.COLORS.PRIMARY,
    success: CONFIG.COLORS.SUCCESS,
    error: CONFIG.COLORS.ERROR
  };
  const bgColor = colors[type] || colors.info;

  // Default icons for each type
  const defaultIcons = {
    info: 'fa-info-circle',
    success: 'fa-check-circle',
    error: 'fa-exclamation-circle'
  };
  const iconClass = icon || defaultIcons[type];

  // Create toast element
  const toast = document.createElement('div');
  toast.className = 'toast-notification';

  // Build toast content with optional icon
  if (iconClass) {
    toast.innerHTML = `
      <i class="fas ${iconClass}"></i>
      <span>${message}</span>
    `;
  } else {
    toast.textContent = message;
  }

  toast.style.cssText = `
    position: fixed;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    background: ${bgColor};
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 0.875rem;
    z-index: ${CONFIG.Z_INDEX_TOAST};
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    opacity: 0;
    transition: opacity 0.3s ease-out;
  `;

  document.body.appendChild(toast);

  // Trigger animation
  setTimeout(() => {
    toast.style.opacity = '1';
  }, 10);

  // Remove after duration
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => toast.remove(), CONFIG.TOAST_FADE_DURATION);
  }, duration);
}

/**
 * Show a loading indicator modal
 * @param {string} message - Loading message to display
 * @returns {HTMLElement} The loading indicator element (for later removal)
 */
export function showLoadingIndicator(message) {
  const indicator = document.createElement('div');
  indicator.id = 'loading-indicator';
  indicator.className = 'loading-indicator';
  indicator.innerHTML = `
    <div class="loading-content">
      <i class="fas fa-spinner fa-spin"></i>
      <span>${message}</span>
    </div>
  `;
  document.body.appendChild(indicator);
  return indicator;
}

/**
 * Hide the loading indicator modal
 */
export function hideLoadingIndicator() {
  const indicator = document.getElementById('loading-indicator');
  if (indicator) {
    indicator.remove();
  }
}

/**
 * Show a save success notification with icon
 * Wrapper for showToast with success styling
 */
export function showSaveSuccess() {
  showToast('Session saved successfully', { type: 'success' });
}

/**
 * Show a session loaded notification
 * Wrapper for showToast with info styling and folder icon
 */
export function showSessionLoaded() {
  showToast('Session loaded', { type: 'info', icon: 'fa-folder-open' });
}

/**
 * Create a modal backdrop element
 * @param {Function} onClose - Callback when backdrop is clicked
 * @returns {HTMLElement} The backdrop element
 */
export function createModalBackdrop(onClose) {
  const backdrop = document.createElement('div');
  backdrop.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${CONFIG.COLORS.OVERLAY_DARKER};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${CONFIG.Z_INDEX_TOAST};
  `;

  if (onClose) {
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) {
        onClose();
      }
    });
  }

  return backdrop;
}

/**
 * Create a modal content container
 * @param {Object} options - Configuration options
 * @param {string} options.title - Modal title
 * @param {string} options.content - Modal HTML content
 * @param {number} options.maxWidth - Max width in pixels (default: 500)
 * @returns {HTMLElement} The modal content element
 */
export function createModalContent({ title, content, maxWidth = 500 }) {
  const container = document.createElement('div');
  container.style.cssText = `
    background: var(--bg-surface);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
    padding: var(--space-xl);
    min-width: ${maxWidth}px;
    max-width: ${maxWidth}px;
    max-height: 80vh;
    overflow-y: auto;
  `;

  container.innerHTML = `
    <h2 style="margin-bottom: var(--space-md); color: var(--text-primary);">${title}</h2>
    ${content}
  `;

  return container;
}

/**
 * Get initials from a token name
 * @param {string} name - Token name (e.g., "Ancient Dragon", "Bob")
 * @returns {string} Initials (max 2 characters, e.g., "AD", "B"), or "?" if empty
 */
export function getTokenInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
}

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} HTML-safe text
 */
export function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
